import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Input,
  Option,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/joy";
import { Link } from "react-router";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import DescriptionRounded from "@mui/icons-material/DescriptionRounded";
import AddRounded from "@mui/icons-material/AddRounded";
import RefreshRounded from "@mui/icons-material/RefreshRounded";
import SettingsRounded from "@mui/icons-material/SettingsRounded";
import HelpOutlineRounded from "@mui/icons-material/HelpOutlineRounded";
import DocumentGuide from "./DocumentGuide";
import LayersRounded from "@mui/icons-material/LayersRounded";
import SearchRounded from "@mui/icons-material/SearchRounded";
import OpenInNewRounded from "@mui/icons-material/OpenInNewRounded";
import SendRounded from "@mui/icons-material/SendRounded";
import ErrorOutlineRounded from "@mui/icons-material/ErrorOutlineRounded";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import {
  Workspace,
  WorkspaceHeader,
  SummaryCards,
} from "../../../components/Workspace/Workspace";
import { LayoutContext } from "../../../context";
import { positionsOf } from "../../../roles";
import { API_URL } from "../../../api";
import { documentRequest } from "../../../settings/documentSettingsApi";
import DocumentExchangeActions from "../../../components/DocumentExchangeActions";
import {
  GenerateDocument,
  BundleDocument,
  DeliverDocument,
} from "./DocumentForms";
import { DOC_TYPES, STATUS, listSlots, money } from "./DocumentUI";

export default function DocumentsPage() {
  const { tokenEmployee, account } = useContext(LayoutContext),
    reduce = useReducedMotion();
  const admin =
    account?.role === "administrator" ||
    positionsOf(account).includes("head_manager");
  const [data, setData] = useState({ results: [], total: 0, summary: {} }),
    [loading, setLoading] = useState(true),
    [error, setError] = useState(""),
    [notice, setNotice] = useState("");
  const [query, setQuery] = useState(""),
    [search, setSearch] = useState(""),
    [kind, setKind] = useState(""),
    [status, setStatus] = useState(""),
    [page, setPage] = useState(0),
    [modal, setModal] = useState(null),
    [busy, setBusy] = useState(null);
  const seq = useRef(0),
    size = 25;
  const scopeRef = useRef(null);
  if (scopeRef.current?.token !== tokenEmployee) scopeRef.current = { token: tokenEmployee };
  const scope = scopeRef.current;
  const currentData = data.owner === scope ? data : { results: [], total: 0, summary: {} };
  const currentModal = modal?.owner === scope ? modal : null;
  const openModal = value => setModal({ ...value, owner: scope });
  useEffect(() => { setModal(null); setNotice(''); setError(''); setBusy(null); }, [tokenEmployee]);
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(query);
      setPage(0);
    }, 300);
    return () => clearTimeout(t);
  }, [query]);
  const load = useCallback(async () => {
    if (!tokenEmployee) return;
    const owner = scope;
    if (owner !== scopeRef.current) return;
    const id = ++seq.current;
    setLoading(true);
    setError("");
    try {
      const p = new URLSearchParams({
        q: search,
        doc_type: kind,
        status,
        offset: page * size,
        limit: size,
      });
      const r = await documentRequest(`?${p}`, { token: tokenEmployee });
      if (!r || !Array.isArray(r.results)) throw new Error('Сервер не вернул список документов. Повторите загрузку.');
      if (owner === scopeRef.current && id === seq.current) setData({ ...r, owner });
    } catch (e) {
      if (owner === scopeRef.current && id === seq.current) setError(e.message);
    } finally {
      if (owner === scopeRef.current && id === seq.current) setLoading(false);
    }
  }, [tokenEmployee, search, kind, status, page, scope]);
  useEffect(() => {
    load();
  }, [load]);
  const action = async (id, fn) => {
    const owner = scope;
    setBusy(id);
    setError("");
    try {
      await fn();
      if (owner === scopeRef.current) await load();
    } catch (e) {
      if (owner === scopeRef.current) setError(e.message);
    } finally {
      if (owner === scopeRef.current) setBusy(null);
    }
  };
  const openPdf = async (d) => {
    const owner = scope;
    const tab = window.open("about:blank", "_blank");
    if (tab) tab.opener = null;
    try {
      const r = await fetch(`${API_URL}/documents/${d.id}/download/`, {
        headers: { Authorization: `Bearer ${tokenEmployee}` },
      });
      if (!r.ok) throw new Error("Не удалось открыть PDF");
      const blob = await r.blob();
      if (owner !== scopeRef.current) { tab?.close(); return; }
      const url = URL.createObjectURL(blob);
      if (tab) tab.location = url;
      else {
        const a = document.createElement("a");
        a.href = url;
        a.download = d.download_filename || `${d.doc_type_display || 'Документ'} ${d.number || d.id}.pdf`;
        a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      tab?.close();
      if (owner === scopeRef.current) setError(e.message);
    }
  };
  const s = currentData.summary || {};
  const done = () => {
    setModal(null);
    setNotice("Документ сохранён");
    load();
  };
  return (
    <Workspace className="doc-workspace">
      <WorkspaceHeader
        title="Документы"
        subtitle="Договоры, счета и закрывающие документы по вашим объектам."
      >
        <Button variant="plain" color="neutral" startDecorator={<HelpOutlineRounded />} onClick={() => openModal({ type: "guide" })}>Инструкция</Button>
        {admin && (
          <Button
            component={Link}
            to="/documents/settings"
            variant="soft"
            startDecorator={<SettingsRounded />}
          >
            Настройки
          </Button>
        )}
        <Button
          variant="outlined"
          color="neutral"
          startDecorator={<LayersRounded />}
          onClick={() => openModal({ type: "bundle" })}
        >
          Общий счёт
        </Button>
        <Button
          startDecorator={<AddRounded />}
          onClick={() => openModal({ type: "generate" })}
        >
          Сформировать
        </Button>
      </WorkspaceHeader>
      <SummaryCards
        items={[
          {
            label: "Всего документов",
            value: s.total ?? currentData.total ?? 0,
            icon: <DescriptionRounded />,
            hint: "С учётом фильтров",
          },
          {
            label: "Сумма документов",
            value: money(s.amount),
            icon: <LayersRounded />,
            hint: "Сумма, не показатель выручки",
          },
          {
            label: "Выдано клиентам",
            value: s.issued || 0,
            icon: <CheckCircleRounded />,
            tone: "success",
          },
          {
            label: "Нужно исправить",
            value: s.errors || 0,
            icon: <ErrorOutlineRounded />,
            tone: "danger",
          },
        ]}
      />
      {error && <Alert color="danger">{error}</Alert>}
      {notice && (
        <Alert
          color="success"
          endDecorator={
            <Button size="sm" variant="plain" onClick={() => setNotice("")}>
              Закрыть
            </Button>
          }
        >
          {notice}
        </Alert>
      )}
      <div className="doc-toolbar">
        <Input
          className="doc-search"
          aria-label="Поиск документов"
          placeholder="Номер, адрес, сделка, телефон или заказчик"
          startDecorator={<SearchRounded />}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <Select
          aria-label="Тип документа"
          value={kind}
          onChange={(_, v) => {
            setKind(v || "");
            setPage(0);
          }}
          slotProps={listSlots}
        >
          <Option value="">Все типы</Option>
          {DOC_TYPES.map(([v, t]) => (
            <Option key={v} value={v}>
              {t}
            </Option>
          ))}
        </Select>
        <Select
          aria-label="Статус документа"
          value={status}
          onChange={(_, v) => {
            setStatus(v || "");
            setPage(0);
          }}
          slotProps={listSlots}
        >
          <Option value="">Все статусы</Option>
          {Object.entries(STATUS).map(([v, [t]]) => (
            <Option key={v} value={v}>
              {t}
            </Option>
          ))}
        </Select>
        <Tooltip title="Обновить">
          <IconButton
            aria-label="Обновить список"
            variant="soft"
            loading={loading}
            onClick={load}
          >
            <RefreshRounded />
          </IconButton>
        </Tooltip>
      </div>
      {loading ? (
        <Stack alignItems="center" sx={{ py: 5 }}>
          <CircularProgress />
          <Typography sx={{ mt: 1 }} level="body-sm">
            Загружаем документы…
          </Typography>
        </Stack>
      ) : !currentData.results.length ? (
        <div className="doc-empty">
          <DescriptionRounded
            sx={{ fontSize: 42, color: "primary.400", mb: 1 }}
          />
          <Typography level="title-lg">
            {search || kind || status
              ? "Документы не найдены"
              : "Здесь будут ваши документы"}
          </Typography>
          <Typography
            level="body-sm"
            sx={{ mt: 1, mb: 2, color: "text.tertiary" }}
          >
            Выберите объект через поиск и проверьте заказчика перед
            формированием.
          </Typography>
          <Button onClick={() => openModal({ type: "generate" })}>
            Создать первый документ
          </Button>
        </div>
      ) : (
        <Stack gap={1.5}>
          <AnimatePresence initial={false}>
            {currentData.results.map((d) => (
              <Box
                component={motion.article}
                key={d.id}
                initial={{ opacity: 0, y: reduce ? 0 : 7 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: reduce ? 0 : 0.18 }}
                className="doc-card"
                sx={{
                  border: "1px solid",
                  borderColor: d.last_error ? "danger.200" : "divider",
                }}
              >
                <div className="doc-card-head">
                  <div className="doc-file-icon">
                    <DescriptionRounded />
                  </div>
                  <div className="doc-card-info">
                    <Stack
                      direction="row"
                      alignItems="center"
                      gap={1}
                      flexWrap="wrap"
                    >
                      <Typography
                        component="h3"
                        level="title-md"
                        fontWeight={750}
                      >
                        {DOC_TYPES.find(([v]) => v === d.doc_type)?.[1]} №{" "}
                        {d.number || d.id}
                      </Typography>
                      <Chip
                        size="sm"
                        color={STATUS[d.status]?.[1] || "neutral"}
                      >
                        {STATUS[d.status]?.[0] || d.status}
                      </Chip>
                      {d.published_to_client && (
                        <Chip size="sm" color="success">
                          В кабинете клиента
                        </Chip>
                      )}
                    </Stack>
                    <Typography
                      level="body-sm"
                      sx={{ mt: 0.7, fontWeight: 600 }}
                    >
                      {d.client_name || "Заказчик не связан"}
                    </Typography>
                    <Typography
                      level="body-sm"
                      sx={{ mt: 0.4, color: "text.tertiary" }}
                    >
                      {d.event_address || "Без объекта"}
                      {d.deal_id ? ` · Сделка №${d.deal_id}` : ""}
                      {d.event ? ` · Событие №${d.event}` : ""}
                    </Typography>
                    <Typography
                      level="body-xs"
                      sx={{ mt: 0.5, color: "text.tertiary" }}
                    >
                      {d.date}
                      {d.client_phone ? ` · ${d.client_phone}` : ""}
                    </Typography>
                  </div>
                  <div className="doc-card-amount">{money(d.total_amount)}</div>
                </div>
                {d.last_error && (
                  <Alert
                    color="danger"
                    sx={{ mt: 2, overflowWrap: "anywhere" }}
                    endDecorator={
                      !d.regeneration_blocker ? (
                        <Button
                          size="sm"
                          color="danger"
                          variant="soft"
                          onClick={() => openModal({ type: "generate", doc: d })}
                        >
                          Исправить
                        </Button>
                      ) : null
                    }
                  >
                    {d.last_error}
                  </Alert>
                )}
                {(d.warnings || []).map((w, i) => (
                  <Alert key={i} color="warning" sx={{ mt: 1 }}>
                    {w}
                  </Alert>
                ))}
                <div className="doc-card-actions">
                  <Button
                    variant="outlined"
                    color="neutral"
                    size="sm"
                    startDecorator={<OpenInNewRounded />}
                    disabled={
                      !d.pdf_url || ["draft", "cancelled"].includes(d.status)
                    }
                    onClick={() => openPdf(d)}
                  >
                    PDF
                  </Button>
                  <Button
                    variant="soft"
                    size="sm"
                    startDecorator={<SendRounded />}
                    disabled={
                      !d.pdf_url || ["draft", "cancelled"].includes(d.status)
                    }
                    onClick={() => openModal({ type: "deliver", doc: d })}
                  >
                    Выдать
                  </Button>
                  <Tooltip
                    title={
                      d.regeneration_blocker ||
                      "Повторить формирование с тем же номером"
                    }
                  >
                    <span>
                      <IconButton
                        aria-label="Пересобрать документ"
                        size="sm"
                        variant="soft"
                        disabled={!!busy || !!d.regeneration_blocker}
                        loading={busy === d.id}
                        onClick={() => {
                          if (
                            window.confirm(
                              `Пересобрать документ №${d.number}? Номер и дата сохранятся.`,
                            )
                          )
                            action(d.id, () =>
                              documentRequest(`${d.id}/regenerate/`, {
                                token: tokenEmployee,
                                method: "POST",
                                data: {},
                              }),
                            );
                        }}
                      >
                        <RefreshRounded />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Select
                    className="doc-status"
                    aria-label="Изменить статус"
                    size="sm"
                    value={d.status}
                    disabled={!!busy}
                    onChange={(_, v) => {
                      if (v && v !== d.status)
                        action(d.id, () =>
                          documentRequest(`${d.id}/status/`, {
                            token: tokenEmployee,
                            method: "POST",
                            data: { status: v },
                          }),
                        );
                    }}
                    slotProps={listSlots}
                  >
                    {(d.status_options || [d.status]).map((v) => (
                      <Option key={v} value={v}>
                        {STATUS[v]?.[0] || v}
                      </Option>
                    ))}
                  </Select>
                </div>
                <DocumentExchangeActions
                  doc={d}
                  token={tokenEmployee}
                  onUpdated={load}
                />
              </Box>
            ))}
          </AnimatePresence>
        </Stack>
      )}
      {!!currentData.total && (
        <div className="doc-pager">
          <Typography level="body-sm">
            {page * size + 1}–{Math.min((page + 1) * size, currentData.total)} из{" "}
            {currentData.total}
          </Typography>
          <Stack direction="row" gap={1}>
            <Button
              variant="soft"
              color="neutral"
              disabled={!page || loading}
              onClick={() => setPage((p) => p - 1)}
            >
              Назад
            </Button>
            <Button
              variant="soft"
              color="neutral"
              disabled={(page + 1) * size >= currentData.total || loading}
              onClick={() => setPage((p) => p + 1)}
            >
              Далее
            </Button>
          </Stack>
        </div>
      )}
      {currentModal?.type === "guide" && <DocumentGuide canConfigure={admin} onClose={() => setModal(null)} onCreate={() => openModal({ type: "generate" })} />}
      {currentModal?.type === "generate" && (
        <GenerateDocument
          token={tokenEmployee}
          repair={currentModal.doc}
          canConfigure={admin}
          onClose={() => setModal(null)}
          onDone={done}
        />
      )}
      {currentModal?.type === "bundle" && (
        <BundleDocument
          token={tokenEmployee}
          onClose={() => setModal(null)}
          onDone={done}
        />
      )}
      {currentModal?.type === "deliver" && (
        <DeliverDocument
          token={tokenEmployee}
          doc={currentModal.doc}
          onPartial={load}
          onClose={() => setModal(null)}
          onDone={done}
        />
      )}
    </Workspace>
  );
}
